import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Button, ErrorNote, Field, MoneyField } from "../../components/ui";
import SearchSelect, { type Choice } from "../../components/SearchSelect";
import { formatMoney, parseMoney } from "../../lib/money";
import { CARD_MAX_CENTS, cardPaymentsConfigured } from "../../lib/stripe";
import { createCharge } from "./charges";
import { useUnsavedGuard, clearUnsavedWork } from "../../lib/unsaved";
import { SaveBar } from "../../components/save";
import { useSaveState } from "../../lib/saving";

interface CustomerOpt {
  id: string; first_name: string; last_name: string;
  is_business: boolean; business_name: string | null;
  phone_cell: string | null; email: string | null;
}
interface VehicleOpt {
  id: string; stock_no: string; year: number;
  make: string; model: string; trim_level: string | null; vin: string | null;
}

const nameOf = (c: CustomerOpt) =>
  c.is_business && c.business_name
    ? c.business_name
    : `${c.last_name}, ${c.first_name}`;

/**
 * Taking a payment that is not against a deal.
 *
 * Two required fields and no more: who it is from, and how much. The vehicle
 * and the description are optional on purpose — a deposit on a car that has
 * not been written up yet may not have a vehicle in the system, and making
 * staff type a reason to get past a form is how receipts end up saying
 * "payment".
 *
 * The customer must already exist. A card taken from somebody who is not in
 * the system is a card nobody can match to a person later, and on a
 * chargeback that is the only question anyone asks.
 */
export default function NewCharge({ onCancel, onDone }: {
  onCancel: () => void;
  onDone: (chargeId: string) => void;
}) {
  const [customers, setCustomers] = useState<CustomerOpt[] | null>(null);
  const [vehicles, setVehicles] = useState<VehicleOpt[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [customerId, setCustomerId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  /* Saving…, then a "Payment created ✓" toast as the payment opens, or why
     not — with the form still filled in. */
  const save_ = useSaveState();

  useEffect(() => {
    Promise.all([
      supabase.from("customers")
        .select("id,first_name,last_name,is_business,business_name,phone_cell,email")
        .order("last_name"),
      /* Every vehicle, not only what is for sale. Money is taken against a
         sold car as often as an available one — a tag fee, a repair promised
         on a We Owe. */
      supabase.from("vehicles")
        .select("id,stock_no,year,make,model,trim_level,vin")
        .order("created_at", { ascending: false }),
    ]).then(([c, v]) => {
      if (c.error) { setLoadErr(`Could not load customers: ${c.error.message}`); return; }
      setCustomers((c.data ?? []) as CustomerOpt[]);
      setVehicles((v.data ?? []) as VehicleOpt[]);
    });
  }, []);

  const cents = parseMoney(amount);
  const dirty = Boolean(customerId || vehicleId || amount.trim() || description.trim());
  useUnsavedGuard(dirty, "this payment");

  const customerChoices: Choice[] = useMemo(() =>
    [...(customers ?? [])]
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b)))
      .map((c) => ({
        id: c.id,
        label: nameOf(c),
        extra: `${c.phone_cell ?? ""} ${c.email ?? ""}`,
      })), [customers]);

  const vehicleChoices: Choice[] = useMemo(() =>
    vehicles.map((v) => ({
      id: v.id,
      label: `${v.year} ${v.make} ${v.model}`
           + `${v.trim_level ? ` ${v.trim_level}` : ""} — ${v.stock_no}`,
      extra: v.vin ?? "",
    })), [vehicles]);

  const tooMuch = cents !== null && cents > CARD_MAX_CENTS;
  const problems: string[] = [];
  if (!customerId) problems.push("Pick a customer.");
  if (cents === null || cents <= 0) problems.push("Enter an amount.");
  if (tooMuch) problems.push("The amount is over the card limit.");

  async function save() {
    if (problems.length || cents === null) return;
    let chargeId = "";
    const ok = await save_.run(async () => {
      const charge = await createCharge({
        customerId,
        vehicleId: vehicleId || null,
        amountCents: cents,
        description: description || null,
      });
      chargeId = charge.id;
    }, { toast: "Payment created" });
    if (!ok) return;
    clearUnsavedWork();
    onDone(chargeId);
  }

  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>;
  if (!customers) return <p className="hint">Loading</p>;

  if (customers.length === 0) {
    return (
      <>
        <h2>Take a payment</h2>
        <p className="hint">
          No customers yet. A payment has to belong to somebody — add the
          customer first, then come back.
        </p>
        <div className="actions">
          <Button variant="ghost" onClick={onCancel}>Back</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2>Take a payment</h2>
      <p className="pagehead-sub">
        Money that is not settling a deal — a deposit, a fee, a repair.
        Payments on a deal go on the deal itself, where the balance is.
      </p>

      {!cardPaymentsConfigured() && (
        <ErrorNote>
          Card payments are not set up on this site, so this payment can be
          recorded but not charged. VITE_STRIPE_PUBLISHABLE_KEY and
          VITE_PAYMENTS_WORKER have to be set.
        </ErrorNote>
      )}

      <SearchSelect
        label="Customer" value={customerId} onChange={setCustomerId}
        choices={customerChoices} placeholder="Who is paying"
        hint="Must already be a customer." />

      <MoneyField
        label="Amount" value={amount} onChange={setAmount}
        hint={`Up to ${formatMoney(CARD_MAX_CENTS)} on a card`}
        error={tooMuch
          ? `Cards are capped at ${formatMoney(CARD_MAX_CENTS)}. Take more than `
            + "that by cashier's check, ACH or wire."
          : null} />

      <Field
        label="What it is for" value={description} onChange={setDescription}
        maxLength={200}
        hint="Optional, and it prints on the receipt. “Deposit on the Civic”, “Tag and title”." />

      <SearchSelect
        label="Vehicle" value={vehicleId} onChange={setVehicleId}
        choices={vehicleChoices} placeholder="Not about a particular vehicle"
        hint="Optional. Adds the vehicle and VIN to the receipt." />

      <SaveBar save={save_} onSave={() => void save()} label="Create payment"
               savingLabel="Creating…" savedLabel="Created" alwaysPrimary
               idle="Not saved yet" disabled={problems.length > 0}>
        <Button variant="ghost" onClick={onCancel} disabled={save_.saving}>Cancel</Button>
      </SaveBar>

      {problems.length > 0 && (
        <ul className="problems">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </>
  );
}
